import type { Artist, Artwork, FrameId, I18nText, Locale, SizeId, TaxonKindPath } from "@artlove365/shared";
import { apiKey } from "@artlove365/shared";

// Dev uses the Vite proxy (same origin); production calls api.artlove365.com.
export const BASE = import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? "" : "https://api.artlove365.com");

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) { super(message); }
}

declare global { interface Window { __API__?: Record<string, unknown> } }

export async function req<T>(path: string, init?: RequestInit): Promise<T> {
  // Data the edge already fetched while rendering this page: use it once, no second round trip.
  if (!init?.method || init.method === "GET") {
    const k = apiKey(path), pre = window.__API__?.[k];
    if (pre !== undefined) { delete window.__API__![k]; return pre as T; }
  }
  const res = await fetch(BASE + path, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { code: string; message: string } } | null;
    throw new ApiError(res.status, body?.error?.code ?? "http_error", body?.error?.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

export type ArtworkFull = Artwork & { heightCm: number | null; widthCm: number | null };
export type ArtistFull = Artist & { nameI18n: I18nText; artworkCount: number; imageUrl: string | null; visible: boolean };
export type Story = { hook: string; sections: { key: string; heading: string; body: string }[]; facts: string[]; sources: { name: string; url: string }[]; locale: string };
export type Guide = { intro: string; faq: { q: string; a: string }[]; sources: { name: string; url: string }[]; locale: string };
export type Relation = "influenced_by" | "influenced" | "student_of" | "student" | "movement";
export type RelatedArtist = { slug: string; name: string; nameI18n: I18nText; relation: Relation; via?: I18nText };
export type RelatedWork = ArtworkFull & { relation: Relation; via: I18nText | null; relatedArtist: I18nText };
export type Paged = { total: number; page: number; pageSize: number };
export type TaxonRef = { kind: string; slug: string; name: I18nText; count: number; linkable: boolean };

export const api = {
  artists: (page = 1) => req<{ artists: ArtistFull[] } & Paged>(`/v1/artists?page=${page}`),
  artist: (slug: string, page = 1, locale = "en") =>
    req<{ artist: ArtistFull; artworks: ArtworkFull[]; movements: { slug: string; name: I18nText }[]; story: Story | null; relatedArtists: RelatedArtist[] } & Paged>(
      `/v1/artists/${encodeURIComponent(slug)}?page=${page}&locale=${locale}`),
  taxa: (kind: TaxonKindPath, page = 1) => req<{ taxa: { slug: string; name: I18nText; count: number }[] } & Paged>(`/v1/taxa/${kind}?page=${page}`),
  taxon: (kind: TaxonKindPath, slug: string, page = 1, locale = "en") =>
    req<{ taxon: { kind: string; slug: string; name: I18nText; count: number }; artworks: ArtworkFull[]; topArtists: { slug: string; name: string; count: number }[]; guide: Guide | null } & Paged>(
      `/v1/taxa/${kind}/${encodeURIComponent(slug)}?page=${page}&locale=${locale}`),
  artworks: (q: { artist?: string; featured?: boolean; q?: string; page?: number; sort?: "new" | "gems"; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (q.q) p.set("q", q.q);
    if (q.page && q.page > 1) p.set("page", String(q.page));
    if (q.sort) p.set("sort", q.sort);
    if (q.limit) p.set("limit", String(q.limit));
    if (q.artist) p.set("artist", q.artist);
    if (q.featured) p.set("featured", "1");
    return req<{ artworks: ArtworkFull[] } & Paged>(`/v1/artworks${p.size ? "?" + p : ""}`);
  },
  bySlugs: (slugs: string[]) => req<{ artworks: ArtworkFull[] }>(`/v1/artworks/by-slugs?slugs=${slugs.slice(0, 200).map(encodeURIComponent).join(",")}`),
  artwork: (slug: string, locale = "en") =>
    req<{ artwork: ArtworkFull; artist: ArtistFull | null; taxa: TaxonRef[]; related: ArtworkFull[]; story: Story | null; relatedArtists: RelatedArtist[]; relatedWorks: RelatedWork[] }>(
      `/v1/artworks/${encodeURIComponent(slug)}?locale=${locale}`),
  upload: (file: File) =>
    req<{ id: string }>("/v1/uploads", { method: "POST", headers: { "Content-Type": file.type }, body: file }),
  createOrder: (body: CreateOrderBody) =>
    req<{ code: string; total: number; checkoutUrl: string | null }>("/v1/orders", {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    }),
  sketchUrl: (code: string, email: string) => `${BASE}/v1/orders/${encodeURIComponent(code)}/sketch?email=${encodeURIComponent(email)}`,
  respondSketch: (code: string, body: { email: string; approve: boolean; feedback?: string }) =>
    req<{ ok: true }>(`/v1/orders/${encodeURIComponent(code)}/sketch-response`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    }),
  order: (code: string, email: string) =>
    req<{ order: OrderView }>(`/v1/orders/${encodeURIComponent(code)}?email=${encodeURIComponent(email)}`),
};

export type Customer = {
  name: string; email: string; phone?: string; address: string; city: string; postalCode: string; country: string;
};
export type CreateOrderBody =
  | { kind: "reproduction"; artworkSlug: string; size: SizeId; frame: FrameId; locale: Locale; notes?: string; customer: Customer }
  | { kind: "custom"; uploadId: string; style: string; subjects: number; size: SizeId; frame: FrameId; locale: Locale; notes?: string; customer: Customer };

export type OrderView = {
  code: string; kind: "reproduction" | "custom"; status: string; artworkSlug: string | null;
  size: string; frame: string; total: number; createdAt: string; trackingNumber: string | null;
  sketchStatus: "pending" | "approved" | "changes_requested" | null;
  events: { status: string; note: string | null; at: string }[];
};
